import { CONTENTFUL_ENV_PARAM } from "frontend/lib/constants";
import useSWR from "swr";

class FetchError extends Error {
  status = 500;
  info: unknown = {};
}

export type UseFetchPropsValue = UseFetchProps | null | false | undefined;

export interface UseFetchProps {
  url: string;
  method?: string;
  params?: Record<string, string | undefined>;
  headers?: Record<string, string>;
  body?: string;
}

export interface UseFetchOptions {
  enabled?: boolean;
}

export async function fetcher({
  url,
  method,
  params,
  headers,
  body,
}: UseFetchProps) {
  const searchParams = new URLSearchParams();
  let hasSearchParams = false;

  if (params) {
    Object.entries(params).forEach(([key, val]) => {
      if (val) {
        searchParams.set(key, val);
        hasSearchParams = true;
      }
    });
  }

  if (url.startsWith("/")) {
    const environmentParameter = new URL(location.href).searchParams.get(
      CONTENTFUL_ENV_PARAM,
    );
    // if the environment is set, then send it, else fallback to the default
    if (environmentParameter) {
      searchParams.set("environment", environmentParameter); // ensures the backend api request uses the same contentful env as frontend
    }
  }

  if (hasSearchParams) url += "?" + searchParams.toString();

  const res = await fetch(url, { method, headers, body });

  const contentType = res.headers.get("content-type") || "";

  // Treat any *json or +json as JSON (application/json, application/*+json, etc.)
  const isJsonContentType = /\bjson\b/i.test(contentType);

  const rawBody = await res.text();

  // "JSON-like" either because the header says *json OR because the body looks like JSON
  const isJsonLike =
    isJsonContentType || (contentType === "" && /^[\s\r\n]*[{[]/.test(rawBody));

  const bodyData = (() => {
    if (!rawBody) return null;
    if (!isJsonLike) return rawBody;

    try {
      return JSON.parse(rawBody);
    } catch {
      // If it *claimed* to be JSON but parsing failed, keep the raw string to surface in error.info / bodyParseFailed.
      return rawBody;
    }
  })();

  if (!res.ok) {
    const error = new FetchError("An error occurred while fetching the data.");
    error.info = {
      status: res.status,
      statusText: res.statusText,
      contentType,
      body: bodyData,
      bodyParseFailed: isJsonLike && typeof bodyData === "string",
      url: res.url,
    };
    error.status = res.status;
    // re-throw error to trigger error on useSWR
    throw error;
  }

  // Don't throw errors for 204/not content responses, but return null instead to avoid SWR retrying on these cases
  if (res.status === 204 || bodyData === null || bodyData === "") {
    return null;
  }

  if (!isJsonLike || typeof bodyData === "string") {
    const error = new FetchError("Expected JSON but got non-JSON response.");
    error.status = res.status;
    error.info = {
      status: res.status,
      statusText: res.statusText,
      contentType,
      body: bodyData,
      bodyParseFailed: isJsonLike && typeof bodyData === "string",
      url: res.url,
    };
    throw error;
  }

  return bodyData;
}

export function useFetch<T = unknown, E = unknown>(
  props: UseFetchPropsValue,
  options: UseFetchOptions = {},
) {
  const { enabled = true } = options;

  return useSWR<T, E>(enabled ? props : null, fetcher, {
    revalidateOnMount: true,

    refreshInterval: 0,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,

    errorRetryCount: 3,
    errorRetryInterval: 1000,

    // return the previous value (if any) when revalidating instead of `undefined`
    keepPreviousData: true,

    onSuccess: (data, key) => {
      console.info("useFetch success", { key, data });
    },

    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      console.info("useFetch error", { key, error, props, retryCount });

      // Never retry on 404.
      if ((error as FetchError).status === 404) return;

      // Only retry up to 3 times.
      if (retryCount >= 3) return;

      // Retry after 5 seconds.
      setTimeout(() => {
        console.info("useFetch retry", { key, props, retryCount });
        void revalidate({ retryCount });
      }, 5000);
    },
  });
}
